import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';

import { FetchError } from '../views/types';
import apiClient from '../requests/api/apiClient';
import { ReduxState } from '../types';
import { setApiCache } from '../actions/apiCacheAction';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiFunction = (...args: any[]) => Promise<{ data: any }>;
type ApiModel = {
  [endpoint: string]: ApiFunction;
};
type ApiClient = {
  [modelName: string]: ApiModel;
};

const typedApiClient = apiClient as unknown as ApiClient;

const useFetchApi = <T>({
  id,
  inputData,
  dependencies,
  modelName,
  modelEndpoint,
  cacheKey,
  cacheTTLms,
}: {
  id?: string;
  inputData?: { [key: string]: string | number };
  dependencies?: Array<boolean | number | string>;
  modelName: string;
  modelEndpoint: string;
  cacheKey?: string;
  cacheTTLms?: number;
}) => {
  const dispatch = useDispatch();
  const cachedEntry = useSelector((state: ReduxState) => (
    cacheKey ? state.apiCache?.entries?.[cacheKey] : undefined
  ));
  const cachedEntryRef = useRef(cachedEntry);
  cachedEntryRef.current = cachedEntry;

  const initialFresh = !!cachedEntry && cacheTTLms !== undefined
    && Date.now() - cachedEntry.timestamp < cacheTTLms;

  const [data, setData] = useState<T | null>(
    initialFresh && cachedEntry ? (cachedEntry.data as T) : null,
  );
  const [loading, setLoading] = useState(!initialFresh);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    let isMounted = true;

    if (id === '') {
      setLoading(false);
      return undefined;
    }

    const entry = cachedEntryRef.current;
    const fresh = !!entry && cacheTTLms !== undefined
      && Date.now() - entry.timestamp < cacheTTLms;

    if (fresh && entry) {
      setData(entry.data as T);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const asyncFunc = async () => {
      try {
        const apiFunction = typedApiClient[modelName]?.[modelEndpoint];
        if (!apiFunction) {
          throw new Error(`Endpoint ${modelEndpoint} not found in model ${modelName}`);
        }

        let dbData;
        if (id && inputData) {
          ({ data: dbData } = await apiFunction(id, inputData));
        } else if (id) {
          ({ data: dbData } = await apiFunction(id));
        } else if (inputData) {
          ({ data: dbData } = await apiFunction(inputData));
        } else {
          ({ data: dbData } = await apiFunction());
        }

        if (!isMounted) return;
        setData(dbData);
        if (cacheKey) dispatch(setApiCache(cacheKey, dbData));
      } catch (err) {
        const { error } = err as FetchError;
        if (error?.message) {
          enqueueSnackbar(error?.message, { variant: 'error' });
        } else {
          const auxError = err as Error;
          enqueueSnackbar(auxError.message, { variant: 'error' });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    asyncFunc();

    return () => {
      isMounted = false;
    };
  }, dependencies || []);

  return { data, loading };
};

export default useFetchApi;
